/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import type { SerializedStyles } from '@emotion/react';

import * as React from 'react';
import LazyLoad from 'react-lazyload';

import Imgix from 'js/components/Imgix';
import { isRightToLeft } from 'js/lib/language';

import { breakpoints, typography2, useMediaQuery } from '@coursera/cds-core';

import { getShouldShowCourseraPlusMerchandisingPlacements } from 'bundles/coursera-plus/utils/subscriptionsForwardDiscoveryExperiment';
import LazyImgix from 'bundles/page/components/shared/LazyImgix';
import { useMWebStreamlinedXDP } from 'bundles/unified-description-page-common/utils/useStreamlinedXDP';

import _t from 'i18n!nls/xdp';

type Props = {
  logo?: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  cta?: React.ReactNode;
  backgroundImage?: string;
  backgroundPosition?: string;
  backgroundColor?: string;
  amountOfCards?: number;
  wrapperContainerStyle?: SerializedStyles;
};

const styles = {
  wrapper: (height: number) => css`
    width: 100%;
    min-height: ${height + 'px'};
    border-radius: 8px;
    position: relative;
    display: inline-block;
    padding: var(--cds-spacing-400);
    contain: content;
  `,
  backgroundContainer: (backgroundPosition?: string) => css`
    position: absolute;
    margin: calc(var(--cds-spacing-400) * -1);
    height: 100%;
    width: 100%;
    z-index: -1;

    .lazyload-wrapper {
      position: absolute;
      bottom: 0;
    }

    img {
      max-width: 100% !important; /* Required to override Imgix styling */
      border-radius: var(--cds-border-radius-100);
      object-fit: cover;
      object-position: ${backgroundPosition || `bottom right`};
    }
  `,
  logo: (amountOfCards?: number) => css`
    padding-bottom: var(--cds-spacing-400);
    width: 100px;
    height: 10px;
    padding-top: ${amountOfCards === 1 ? 'var(--cds-spacing-600)' : ''};
    ${breakpoints.down('xs')} {
      padding-top: 0;
    }
  `,
  title: (logo?: string) => css`
    padding-bottom: var(--cds-spacing-200);
    padding-top: ${logo ? '0px' : 'var(--cds-spacing-400)'};
  `,
  link: css`
    padding-top: var(--cds-spacing-400);
  `,
  description: css`
    padding-bottom: var(--cds-spacing-200);
    max-width: 510px;
  `,
  mWebWrapper: (backgroundColor?: string) => css`
    padding: var(--cds-spacing-300);
    background-color: ${backgroundColor || 'transparent'};

    ${breakpoints.down('md')} {
      img {
        object-position: 100% !important;
        object-fit: contain !important;
      }
    }
  `,
  mWebBackgroundContainer: (isRTL?: boolean) => css`
    margin: calc(var(--cds-spacing-300) * -1);

    img {
      transform: ${isRTL ? 'scale(-1, 1)' : 'none'};
    }
  `,
  mWebTitle: css`
    padding-top: 0;

    h2 {
      ${typography2.subtitleLarge};
    }

    ${breakpoints.down('sm')} {
      h2 {
        max-width: 70%;
        padding-bottom: var(--cds-spacing-300);
      }
    }

    ${breakpoints.down(338)} {
      h2 {
        max-width: 75%;
        font: ${typography2.subtitleMedium.font};
      }
    }
  `,
};

const getCardQuality = (amountOfCards?: number) => {
  switch (amountOfCards) {
    case 3:
      return '30';
    case 2:
      return '50';
    case 1:
      return '80';
    default:
      return '50';
  }
};

const UpsellCard = React.forwardRef(
  (
    {
      logo,
      title,
      description,
      cta,
      backgroundImage,
      backgroundPosition,
      backgroundColor,
      amountOfCards,
      wrapperContainerStyle,
    }: Props,
    ref?: React.Ref<HTMLDivElement>
  ) => {
    const isRTL = isRightToLeft(_t.getLocale());
    const isMWebStreamlinedXDP = useMWebStreamlinedXDP();
    const isMobileSubscriptionFirst =
      useMediaQuery(breakpoints.down('sm')) && getShouldShowCourseraPlusMerchandisingPlacements();
    const imgWidth = isMWebStreamlinedXDP || isMobileSubscriptionFirst ? 976 : 1344;
    let imgHeight = amountOfCards === 1 ? 400 : 548;

    if (isMWebStreamlinedXDP) {
      imgHeight = 178;
    } else if (isMobileSubscriptionFirst) {
      imgHeight = 190;
    }

    if (!backgroundImage || !title) {
      return null;
    }

    return (
      <div
        css={[
          styles.wrapper(imgHeight),
          isMWebStreamlinedXDP || isMobileSubscriptionFirst ? styles.mWebWrapper(backgroundColor) : '',
          wrapperContainerStyle,
        ]}
        ref={ref}
      >
        <div
          css={[
            styles.backgroundContainer(backgroundPosition),
            isMWebStreamlinedXDP || isMobileSubscriptionFirst ? styles.mWebBackgroundContainer(isRTL) : '',
          ]}
        >
          <LazyLoad offset={100} once>
            <Imgix
              src={backgroundImage}
              alt="" // No alt text required; used only for visual appeal
              width={imgWidth}
              height={imgHeight}
              maxWidth={imgWidth}
              maxHeight={imgHeight}
              imgParams={{ quality: getCardQuality(amountOfCards) }}
            />
          </LazyLoad>
        </div>
        {logo && (
          <div css={styles.logo(amountOfCards)}>
            <LazyImgix src={logo} alt={_t('Coursera Plus')} maxWidth={120} maxHeight={15} />
          </div>
        )}
        <div css={[isMWebStreamlinedXDP || isMobileSubscriptionFirst ? styles.mWebTitle : styles.title(logo)]}>
          {title}
        </div>
        {description && !isMWebStreamlinedXDP && !isMobileSubscriptionFirst && (
          <div css={styles.description}>{description}</div>
        )}
        <div css={amountOfCards === 1 && styles.link}>{cta}</div>
      </div>
    );
  }
);

export default UpsellCard;
